import { BUILD_ENVIRONMENTS } from '@src/config/constants'
import { CONFIG_BUILD_ENV } from '@src/config/env'
import { QuinceRouter } from '@src/modules/navigation'
import {
  EFilterKeys,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import { PRODUCT_FEED_TYPE } from '@src/quince-site-common/src/services/productFeedService/productFeedService.types'
import { SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_SLUGS } from '@src/quince-site-common/src/utils/constants'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { pickBy } from '@src/utils/functions/utils'

import { PFY_DYNAMIC_COLLECTION_ID_QA, PFY_DYNAMIC_COLLECTION_ID_PROD } from './collection.constants'
import Collection, { SubCollection } from './domains/Collection'

export function isCustomSection(section: CustomSection | SubCollection): section is CustomSection {
  return section.typename === 'CustomSection'
}

export function shouldShowBorder(currentIndex: number, sections: (SubCollection | CustomSection)[]) {
  const nextSection = sections[currentIndex + 1]
  const nextIsCustomSection = nextSection && isCustomSection(nextSection)
  const nextIsFaqSection = nextIsCustomSection && nextSection.sectionType === CUSTOM_SECTION_TYPE.ACCORDION
  const isLastSection = currentIndex === sections.length - 1
  const showBorderBottom = !nextIsFaqSection && !isLastSection
  return showBorderBottom
}

export const getSubCollections = (subCollections: (SubCollection | CustomSection)[]) => {
  // get only subcollections
  return subCollections.filter(sc => sc.typename !== 'CustomSection')
}

export const shouldShowSubCollectionCards = (subCollections: SubCollection[] = []) => {
  if (subCollections.length <= 1) {
    return false
  }
  const subCollectionWithCardData = subCollections.filter(sc => sc.imageDesktop !== undefined)
  return subCollections.length === subCollectionWithCardData.length
}

export const shouldEnrichCustomSection = (customSection: CustomSection) =>
  (customSection.sectionType === CUSTOM_SECTION_TYPE.PRODUCT_LIST_TWO ||
    customSection.sectionType === CUSTOM_SECTION_TYPE.PRODUCT_LIST_THREE) &&
  (customSection.productFeedType == PRODUCT_FEED_TYPE.CUSTOMER_TOP_SIMILAR_PRODUCTS ||
    customSection.productFeedType == PRODUCT_FEED_TYPE.FBT)

export const getEnrichedCustomSection = (
  customSection: CustomSection,
  subCollections: (SubCollection | CustomSection)[],
): CustomSection => {
  return {
    ...customSection,
    productFeedType: PRODUCT_FEED_TYPE.FBT,
    productFeedMetaData: {
      productCards: subCollections
        .filter(s => s.typename !== 'CustomSection')
        .map(s => s)
        .map(s => s.productCards)
        .flat(),
    },
  }
}

export const getFormattedSlug = (slug: string) => {
  let finalSlug = slug
  if (slug.startsWith('/')) {
    finalSlug = slug.slice(1)
  }
  return finalSlug
}

export const getShowSecondImageOnProductTilesValue = (slug: string): boolean => {
  return SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_SLUGS.includes(slug)
}

export const getCollectionCacheKey = (pathname: string, searchParams: string): string => {
  if (!searchParams) return pathname
  return `${pathname}?${searchParams}`
}

// TODO: Move this to functions/utils.ts
export const isUndefined = (value: unknown) => value === undefined

export const getPFYDynamicCollectionId = () => {
  if (CONFIG_BUILD_ENV === BUILD_ENVIRONMENTS.QA) return PFY_DYNAMIC_COLLECTION_ID_QA
  return PFY_DYNAMIC_COLLECTION_ID_PROD
}

export const getUpdatedSelectedFilters = ({
  filter,
  sort,
  selectedFilters,
  sortBy,
  options,
  collectionPage,
  isNewCLPFiltersExperimentActive,
  router,
}: {
  filter: UpdateQueryParams['filter']
  sort: UpdateQueryParams['sort']
  selectedFilters: SelectedFilter
  sortBy: string
  router: QuinceRouter
  options?: { skipFilterClickEvent?: boolean }
  collectionPage?: Collection
  isNewCLPFiltersExperimentActive?: boolean
}) => {
  let updatedSelectedFilters = selectedFilters
  let updatedSortBy = sortBy

  if (filter) {
    switch (filter.type) {
      case 'remove': {
        updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
          selectedFilters,
          filter.value,
          filter.name,
        )
        trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
          filter_key: filter.name,
          filter_value: filter.value,
        })
        break
      }
      case 'toggle': {
        if (filter.name !== 'productType') {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            filter.name,
            filter.value,
          )
        }
        if (filter.name === 'productType' && isNewCLPFiltersExperimentActive) {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            EFilterKeys.SUBCOLLECTIONS,
            filter.value,
          )
        }
        if (!options?.skipFilterClickEvent) {
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.label,
            value: filter.value,
            context: {
              ...(collectionPage && {
                collectionSlug: collectionPage.slug,
                collectionTitle: collectionPage.title,
              }),
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
              filter_key: filter.name,
              filter_value: filter.value,
            },
          })
          if (
            !isNewCLPFiltersExperimentActive &&
            SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value)
          ) {
            trackEvent(TELEMETRY_EVENT_NAMES.FILTER_APPLIED, {
              filter_key: filter.name,
              filter_value: filter.value,
            })
          }
        }

        // ISSUE: @tech-debt productType shouldn't be a filter and isn't a filter - forced custom logic shouldn't be added
        if (filter.name === 'productType' && !isNewCLPFiltersExperimentActive)
          router.push(`/${filter.value}?jumplink=true`)
        break
      }

      case 'reset': {
        updatedSelectedFilters = new SelectedFilter({})
        trackEvent(TELEMETRY_EVENT_NAMES.FILTERS_CLEARED, {})
        break
      }

      case 'overwrite': {
        updatedSelectedFilters = filter.updatedFilters
        break
      }

      case 'removeAll': {
        updatedSelectedFilters = SelectedFilter.removeAllValuesFromSelectedFilter(selectedFilters, filter.name)
        if (!options?.skipFilterClickEvent) {
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.label,
            value: filter.value,
            context: {
              ...(collectionPage && {
                collectionSlug: collectionPage.slug,
                collectionTitle: collectionPage.title,
              }),
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters as Record<string, unknown>, v => v !== undefined),
              filter_key: filter.name,
              filter_value: filter.value,
            },
          })
        }
        break
      }
      default:
        break
    }
  }

  if (sort) {
    switch (sort.type) {
      case 'toggle': {
        const sortValue = sort.value
        updatedSortBy = sortValue === sortBy ? SORT_BY.FEATURED.value : sortValue
        break
      }
      case 'reset': {
        updatedSortBy = SORT_BY.FEATURED.value
        break
      }
      default:
        break
    }

    const isDefaultOption = updatedSortBy === SORT_BY.FEATURED.value
    if (!isDefaultOption) {
      trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
        sort_used: updatedSortBy,
      })
    }
  }

  return { updatedSelectedFilters, updatedSortBy }
}
